import React from 'react';
import PropTypes from 'prop-types';
import { SearchField, Typography } from '@statsbomb/kitbag-components';
import { useStore } from '../../../../../store/useStore';
import {
  ScoutHeader,
  ScoutSearch,
  DownloadButtonWrapper,
} from '../ScoutPlayers.styles';
import DownloadCSVButton from '../../../../../components/buttons/DownloadCSV';
import { trackContentSearchEvent } from '../../../../../utils/helpers/snowplow';
import { useExportSeasonInfo } from '../../../../../utils/hooks/useExportSeasonInfo';
import {
  formatPlayersCSV,
  sortPlayers,
} from '../ScoutPlayers.dataManipulation';
import { ROSTER_POSITIONS } from '../../../../../utils/constants/positions';
import { DEFAULT_SCOUT_POSITION } from '../../ScoutResults.constants';

const ScoutPlayersHeader = ({ players, headers }) => {
  const playersCount = useStore((s) => s.scoutPlayersCount);
  const searchValue = useStore((s) => s.scoutTableSearchText);
  const setScoutTableSearchText = useStore((s) => s.setScoutTableSearchText);
  const isLoading = useStore((s) => s.scoutPlayersLoading);
  const activePage = useStore((s) => s.scoutPlayersActivePage);
  const setActivePage = useStore((s) => s.setScoutPlayersActivePage);
  const sortKey = useStore((s) => s.scoutPlayersSortKey);
  const sortDirection = useStore((s) => s.scoutPlayersSortDirection);
  const showFavourites = useStore((s) => s.showFavourites);
  const scoutFavourites = useStore((s) => s.scoutFavourites);

  const { competitionLevel } = useExportSeasonInfo({
    asObject: true,
  });

  const playersAreFavourites = players.filter(
    (p) => Array.isArray(scoutFavourites) && scoutFavourites.includes(p.id)
  );

  const seasonName = playersCount > 0 ? players[0]?.seasonName : '-';
  const csvName = `Scout for ${ROSTER_POSITIONS[DEFAULT_SCOUT_POSITION].name} ${seasonName}`;

  const csvData = formatPlayersCSV(
    sortPlayers(
      showFavourites ? playersAreFavourites : players,
      sortKey,
      sortDirection
    ),
    headers
  );

  const handleSearch = (e) => {
    if (activePage !== 1) {
      setActivePage(1);
    }
    trackContentSearchEvent(e.target.value, 'Search bar');
    setScoutTableSearchText(e.target.value);
  };

  return (
    <ScoutHeader>
      <Typography
        variant="headingRegular"
        data-testid="scout-results-player-count"
      >
        {`${playersCount} ${competitionLevel ?? ''} player${
          playersCount !== 1 ? 's' : ''
        } matching your criteria`}
      </Typography>
      <ScoutSearch>
        <SearchField
          value={searchValue}
          isLoading={isLoading}
          onChange={handleSearch}
          placeholder="Search for players"
          searchIconTitle="search"
          onClear={() => {
            setScoutTableSearchText('');
            setActivePage(1);
          }}
        />
      </ScoutSearch>
      <DownloadButtonWrapper>
        <DownloadCSVButton
          data={csvData}
          headers={headers}
          fileName={csvName}
          iconSize="small"
          buttonSize="medium"
        />
      </DownloadButtonWrapper>
    </ScoutHeader>
  );
};

ScoutPlayersHeader.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  players: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  headers: PropTypes.array.isRequired,
};

export default ScoutPlayersHeader;
